import React, { FC, useCallback, useEffect, useState } from 'react';

import LoadingAnimation from 'components/loading-animation';
import { BackArrowHistory } from 'components/navigation/back-arrow';
import { useFocusEffect } from 'hooks/navigation/use-focus-effect';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { useRoute } from 'hooks/navigation/use-route';
import { appendObjectToQueryString } from 'hooks/navigation/utils';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import { useLocale } from 'state/intl';
import useRewardsEvaluation from 'state/loyalty/hooks/use-loyalty-rewards-evaluation';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { Region } from 'utils/environment/types';
import { EventName, emitEvent } from 'utils/event-hub';
import { routes } from 'utils/routing';

import { ONLY_SHOW_REDEEM_REWARD_IN_RESTAURANT_CTA } from '../loyalty-incentives-components/incentive-details/constants';
import LoyaltyIncentiveNotAvailableModal from '../loyalty-incentives-components/loyalty-incentive-not-available-modal';
import { LoyaltyNotEnrolled } from '../loyalty-not-enrolled';

import { useFilteredRewardCategories } from './loyalty-rewards';
import { LoyaltyRewardsRedesignContent } from './loyalty-rewards-redesign-content';
import { LoyaltyRewardsRedesignView } from './loyalty-rewards-redesign.view';
import { LoyaltyLoaderContainer } from './loyalty-rewards.styled';
import { ILoyaltyRewardsProps } from './types';

/**
 *
 * LoyaltyRewards displays a list of available rewards that can be redeemed at checkout
 *
 */
const LoyaltyRewardsRedesign: FC<React.PropsWithChildren<ILoyaltyRewardsProps>> = ({
  title,
  isWidget = false,
}) => {
  const { user, loading: userDataIsLoading } = useAuthContext();
  const [selectedRewardIdFromWidget, setSelectedRewardIdFromWidget] = useState('');
  const {
    engineRewardsMap,
    loading: rewardDataIsLoading,
    sanityRewardsMap,
    refetchRewards,
  } = useLoyaltyRewardsList();
  const isDesktop = useIsDesktopBreakpoint();

  useFocusEffect(
    useCallback(() => {
      emitEvent(EventName.SCREEN_RENDER, {
        screenId: 'loyaltyRewards',
      });
    }, [])
  );

  const { rewardsEvaluationMap } = useRewardsEvaluation();

  const { logRBIEvent } = useMParticleContext();
  const { region } = useLocale();
  const { navigate, linkTo } = useNavigation();

  const loyaltyId = user?.loyaltyId;
  const shouldRedirectToRoyalPerks = !loyaltyId && region === Region.US;

  useEffect(() => {
    // Redirect to royal perks if unauthenticated and BK US
    if (!userDataIsLoading && shouldRedirectToRoyalPerks) {
      navigate(routes.royalPerks, { replace: true });
    }
  }, [navigate, shouldRedirectToRoyalPerks, userDataIsLoading]);

  const { params } = useRoute<{ rewardId: string; category: string }>();
  const { rewardId } = params || {};

  // If using as widget, the reward ID is saved in state
  const selectedRewardId = isWidget ? selectedRewardIdFromWidget : rewardId;

  // Local state for opened accordions
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const rewardCategories = useFilteredRewardCategories();

  const handleRewardOnPress = useCallback(
    (incentiveId: string, incentiveName: string, incentiveSanityId: string) => {
      if (selectedRewardId !== incentiveSanityId) {
        logRBIEvent({
          name: CustomEventNames.REWARD_SELECTED,
          type: EventTypes.Other,
          attributes: {
            engineId: incentiveId,
            sanityId: incentiveSanityId,
            name: incentiveName,
          },
        });
        if (isWidget) {
          setSelectedRewardIdFromWidget(incentiveSanityId);
          return;
        }
      }

      const onlyShowRedeemRewardValue = params[ONLY_SHOW_REDEEM_REWARD_IN_RESTAURANT_CTA];

      const objectParams =
        ONLY_SHOW_REDEEM_REWARD_IN_RESTAURANT_CTA && onlyShowRedeemRewardValue
          ? {
              [ONLY_SHOW_REDEEM_REWARD_IN_RESTAURANT_CTA]: onlyShowRedeemRewardValue,
            }
          : {};
      const incentiveDetailRoute = appendObjectToQueryString(
        `${routes.loyaltyRewardsList}/${incentiveSanityId}`,
        objectParams
      );
      linkTo(incentiveDetailRoute);
    },
    [isWidget, linkTo, logRBIEvent, params, selectedRewardId]
  );

  if (userDataIsLoading || rewardDataIsLoading) {
    return (
      <LoyaltyLoaderContainer>
        <LoadingAnimation />
      </LoyaltyLoaderContainer>
    );
  }
  if (shouldRedirectToRoyalPerks) {
    return null;
  }

  if (!loyaltyId) {
    return <LoyaltyNotEnrolled />;
  }

  const selectedReward = sanityRewardsMap?.[selectedRewardId || ''];
  if (sanityRewardsMap && selectedRewardId && !selectedReward) {
    return <LoyaltyIncentiveNotAvailableModal />;
  }

  if (isWidget) {
    return (
      <>
        <LoyaltyRewardsRedesignContent
          selectedReward={selectedReward}
          handleOnPress={handleRewardOnPress}
          sanityRewardsMap={sanityRewardsMap}
          rewardCategories={rewardCategories}
          engineRewardsMap={engineRewardsMap}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          refetchRewards={refetchRewards}
          isRefreshingRewards={rewardDataIsLoading}
          rewardsEvaluationMap={rewardsEvaluationMap}
          isWidget={isWidget}
          title={title}
          setSelectedRewardIdFromWidget={setSelectedRewardIdFromWidget}
        />
      </>
    );
  }

  return rewardCategories?.length && sanityRewardsMap ? (
    <>
      {isDesktop && selectedRewardId && <BackArrowHistory />}
      <LoyaltyRewardsRedesignView
        selectedReward={selectedReward}
        handleOnPress={handleRewardOnPress}
        sanityRewardsMap={sanityRewardsMap}
        rewardCategories={rewardCategories}
        engineRewardsMap={engineRewardsMap}
        selectedCategories={selectedCategories}
        setSelectedCategories={setSelectedCategories}
        refetchRewards={refetchRewards}
        isRefreshingRewards={rewardDataIsLoading}
        rewardsEvaluationMap={rewardsEvaluationMap}
        isWidget={isWidget}
        title={title}
        setSelectedRewardIdFromWidget={setSelectedRewardIdFromWidget}
      />
    </>
  ) : null;
};

export default LoyaltyRewardsRedesign;
